/**
 * Channel Service - API calls for admin team chats (channels)
 */

import axiosInstance from './axiosConfig';

// ============================================================================
// TYPES
// ============================================================================

export interface ChannelMember {
  user_id: string;
  display_name: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
  last_read_at?: string;
  muted?: boolean;
  profile_image?: string;
}

export interface Channel {
  _id: string;
  channel_id: string;
  name?: string;
  description?: string;
  type: 'dm' | 'group';
  scope: 'admin' | 'client';
  members: ChannelMember[];
  created_by: string;
  /** @deprecated Legacy — case chats use linked_client_id. */
  linked_so_id?: string;
  /** When set, this group channel is the case chat for the client (one per client). */
  linked_client_id?: string;
  last_message_at?: string;
  last_message_preview?: string;
  avatar_url?: string;
  unread_count?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelMessage {
  _id: string;
  message_id: string;
  channel_id: string;
  sender_id: string;
  sender_name: string;
  sender_avatar?: string;
  message_type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'system';
  content?: string;
  media?: {
    filename: string;
    mime_type: string;
    size: number;
    blob_url: string;
    blob_name: string;
  };
  system_event?: {
    type: string;
    actor_id?: string;
    target_id?: string;
    metadata?: Record<string, any>;
  };
  read_by?: Array<{ user_id: string; read_at: string }>;
  timestamp: string;
  edited_at?: string;
  deleted_at?: string;
}

export interface AvailableUser {
  _id: string;
  name: string;
  username: string;
  profile_image?: string;
  role: string;
}

// ============================================================================
// API CALLS
// ============================================================================

/**
 * List channels for the current user (fetches all pages for admin scope).
 */
export async function listChannels(
  scope: 'admin' | 'client' = 'admin',
  pageSize: number = 200
): Promise<{ channels: Channel[]; total: number }> {
  const channels: Channel[] = [];
  let offset = 0;
  let total = 0;

  for (;;) {
    const response = await axiosInstance.get('/channels', {
      params: { scope, limit: pageSize, offset }
    });
    const batch = (response.data.channels || []) as Channel[];
    total = response.data.total ?? batch.length;
    channels.push(...batch);
    if (batch.length === 0 || channels.length >= total) break;
    offset += batch.length;
  }

  return { channels, total };
}

/**
 * Get a single channel
 */
export async function getChannel(channelId: string): Promise<Channel> {
  const response = await axiosInstance.get(`/channels/${channelId}`);
  return response.data.channel;
}

/**
 * Create a new channel
 */
export async function createChannel(data: {
  type: 'dm' | 'group';
  scope: 'admin' | 'client';
  name?: string;
  description?: string;
  member_ids: string[];
}): Promise<Channel> {
  const response = await axiosInstance.post('/channels', data);
  return response.data.channel;
}

/**
 * Update a channel
 */
export async function updateChannel(
  channelId: string,
  data: { name?: string; description?: string; avatar_url?: string }
): Promise<Channel> {
  const response = await axiosInstance.patch(`/channels/${channelId}`, data);
  return response.data.channel;
}

/**
 * Get messages for a channel
 */
export async function getChannelMessages(
  channelId: string,
  options: { limit?: number; before?: string; after?: string } = {}
): Promise<{ messages: ChannelMessage[]; hasMore: boolean }> {
  const response = await axiosInstance.get(`/channels/${channelId}/messages`, {
    params: options
  });
  return response.data;
}

/**
 * Send a message to a channel
 */
export async function sendChannelMessage(
  channelId: string,
  data: {
    message_type: 'text' | 'image' | 'video' | 'audio' | 'document';
    content?: string;
    media?: {
      filename: string;
      mime_type: string;
      size: number;
      blob_url: string;
      blob_name: string;
    };
  }
): Promise<ChannelMessage> {
  const response = await axiosInstance.post(`/channels/${channelId}/messages`, data);
  return response.data.message;
}

/** Multipart upload (image, video, audio, PDF, docs) — same as admin Team Chat UI. */
export async function sendChannelMessageWithMedia(
  channelId: string,
  file: File,
  mediaType: 'image' | 'video' | 'audio' | 'document',
  content?: string
): Promise<ChannelMessage> {
  const formData = new FormData();
  formData.append('media', file);
  formData.append('message_type', mediaType);
  if (content) {
    formData.append('content', content);
  }
  const response = await axiosInstance.post(`/channels/${channelId}/messages`, formData);
  return response.data.message;
}

/**
 * Edit a text message in a channel (sender only; backend enforced).
 */
export async function updateChannelMessage(
  channelId: string,
  messageId: string,
  content: string
): Promise<ChannelMessage> {
  const response = await axiosInstance.patch(`/channels/${channelId}/messages/${messageId}`, {
    content
  });
  return response.data.message;
}

/**
 * Soft-delete a channel message (sender or channel admin/owner; backend enforced).
 */
export async function deleteChannelMessage(channelId: string, messageId: string): Promise<void> {
  await axiosInstance.delete(`/channels/${channelId}/messages/${messageId}`);
}

/**
 * Mark all messages in a channel as read
 */
export async function markChannelAsRead(channelId: string): Promise<void> {
  await axiosInstance.patch(`/channels/${channelId}/read`);
}

/**
 * Add a member to a channel
 */
export async function addChannelMember(
  channelId: string,
  userId: string
): Promise<Channel> {
  const response = await axiosInstance.post(`/channels/${channelId}/members`, { user_id: userId });
  return response.data.channel;
}

/**
 * Remove a member from a channel
 */
export async function removeChannelMember(
  channelId: string,
  userId: string
): Promise<Channel> {
  const response = await axiosInstance.delete(`/channels/${channelId}/members/${userId}`);
  return response.data.channel;
}

/**
 * Ensure the user is a channel member (e.g. SO team chat: RM/watchers may be listed on the order
 * but not yet on the Channel document). Safe to call repeatedly.
 */
export async function ensureChannelMembership(channelId: string, userId: string): Promise<void> {
  let needsJoin = true;
  try {
    const ch = await getChannel(channelId);
    needsJoin = !ch.members?.some((m) => m.user_id === userId);
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } }).response?.status;
    // Channel was deleted or deactivated — do not attempt POST /members (also 404).
    if (status === 404) {
      throw e;
    }
    // Non-members may get 403 on GET; joining fixes access.
    needsJoin = true;
  }

  if (!needsJoin) return;

  try {
    await addChannelMember(channelId, userId);
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: { message?: string; error?: string } } };
    const text = `${err.response?.data?.message || ''} ${err.response?.data?.error || ''}`.toLowerCase();
    if (text.includes('already') || text.includes('member')) {
      return;
    }
    throw e;
  }
}

/**
 * Transfer group ownership to another member
 */
export async function transferChannelOwnership(
  channelId: string,
  newOwnerId: string
): Promise<Channel> {
  const response = await axiosInstance.patch(`/channels/${channelId}/transfer-ownership`, {
    new_owner_id: newOwnerId
  });
  return response.data.channel;
}

/**
 * Get available users for creating channels
 */
export async function getAvailableUsers(
  scope: 'admin' | 'client' = 'admin',
  search?: string
): Promise<AvailableUser[]> {
  const response = await axiosInstance.get('/channels/available-users', {
    params: { scope, search }
  });
  return response.data.users;
}
